import { createContext } from '@direct-frontend/modules-context';
import type { PubSubEvent } from '@direct-frontend/stdlib';

/**
 * Обработчик события.
 *
 * @typeParam Payload - Тип данных, которые могут быть переданы вместе с событием
 */
type EventHandler<Payload> = Parameters<PubSubEvent<Payload>['subscribe']>[0];

/**
 * Держатель списка обработчиков. Список хранится в поле объекта, чтобы его можно было
 * положить в контекст один раз и дальше изменять без вызова setContext.
 *
 * @typeParam Payload - Тип данных, которые могут быть переданы вместе с событием
 */
type EventHandlersHolder<Payload> = {
    handlers: EventHandler<Payload>[];
};

/**
 * Создает событие, подписки на которое живут не дольше контекста, в котором были созданы.
 *
 * В отличие от `createEvent` из stdlib, список обработчиков хранится не в замыкании (то есть
 * не глобально на весь процесс), а в контексте `modules-context`.
 *
 * На сервере контекст создается на каждый SSR-запрос (`AsyncLocalStorage`), поэтому обработчики
 * вместе со всем, что они захватили в замыкании (потоки, `getStore`, per-request redux-стор),
 * освобождаются сборщиком мусора по завершении запроса — отдельный teardown-хук не нужен.
 * Побочно это исправляет и доставку: событие одного запроса больше не видно в другом запросе.
 *
 * На клиенте `modules-context` хранит контекст в единственном объекте на уровне модуля,
 * поэтому событие ведет себя ровно так же, как обычное глобальное событие — публичное
 * поведение событий на клиенте не меняется.
 *
 * `subscribe` и `publish` на сервере обязаны вызываться внутри контекста запроса: вызов снаружи
 * — это баг, поэтому `modules-context` бросает исключение. На клиенте контекст есть всегда.
 *
 * @typeParam Payload - Тип данных, которые могут быть переданы вместе с событием
 * @param name - Уникальное имя события, используется как ключ в контексте
 * @returns Событие с методами `.subscribe()` и `.publish()`
 */
export function createRequestScopedEvent<Payload = never>(name: string): PubSubEvent<Payload> {
    const { getContext } = createContext<EventHandlersHolder<Payload>>({
        name: `@direct-frontend/modules-store/${name}`,
        getInitialValue: () => ({ handlers: [] }),
    });

    return {
        publish(...args) {
            // Итерируемся по копии списка: обработчик может подписаться или отписаться прямо
            // во время доставки, и это не должно влиять на текущую рассылку.
            for (const eventHandler of [...getContext().handlers]) {
                eventHandler(...args);
            }
        },
        subscribe(eventHandler) {
            // Держатель фиксируется на момент подписки, чтобы отписка сработала даже если она
            // произойдет вне контекста запроса. Ссылка живет внутри самой функции отписки,
            // то есть не дольше подписчика, и не удерживает контекст запроса глобально.
            const holder = getContext();

            holder.handlers.push(eventHandler);

            let active = true;

            return function unsubscribe() {
                if (!active) {
                    return;
                }

                active = false;

                // Удаляем ровно одну запись: один и тот же обработчик можно подписать несколько
                // раз, и каждая отписка должна снимать только свою подписку.
                const index = holder.handlers.indexOf(eventHandler);

                if (index !== -1) {
                    holder.handlers.splice(index, 1);
                }
            };
        },
    };
}
